import React, { useState, useEffect } from 'react';
import { Dialog, DialogContent, DialogTitle, DialogDescription } from './ui/dialog';
import { Button } from './ui/button';
import { Sparkles, BookOpen, Video, Users, ChevronRight, ChevronLeft, CheckCircle2 } from 'lucide-react';
import { cn } from '../lib/utils';

const STORAGE_KEY = 'pdd_parvularia_tour_seen_v1';

const SLIDES = [
    {
        icon: Sparkles,
        eyebrow: 'Bienvenida educadora de párvulos',
        title: 'Tu Portafolio Digital Docente 2026',
        body: 'Diseñamos un espacio dedicado a Educación Parvularia con los 3 módulos oficiales, sus tareas y las rúbricas que evalúa CPEIP. Todo aislado del resto de niveles para que tu trabajo sea 100% pertinente a párvulos.',
        gradient: 'from-fuchsia-500 to-pink-600',
        color: 'fuchsia',
    },
    {
        icon: BookOpen,
        eyebrow: 'Módulo 1',
        title: 'Planificación, evaluación, análisis y reflexión',
        body: 'Aborda la planificación de experiencias educativas, la evaluación de aprendizajes, el análisis de trabajos de los párvulos y la reflexión sobre tu propia práctica pedagógica. Contiene las Tareas 1, 2 y 3.',
        gradient: 'from-indigo-500 to-blue-600',
        color: 'indigo',
        items: [
            'Tarea 1 · Planificación de experiencias educativas',
            'Tarea 2 · Evaluación de los aprendizajes',
            'Tarea 3 · Análisis y reflexión pedagógica',
        ],
    },
    {
        icon: Video,
        eyebrow: 'Módulo 2',
        title: 'Grabación de una experiencia para el aprendizaje',
        body: 'Registro audiovisual de una experiencia educativa completa, donde demuestres tu mediación pedagógica en interacción real con los párvulos. Contiene la Tarea 4.',
        gradient: 'from-rose-500 to-red-600',
        color: 'rose',
        items: [
            'Tarea 4 · Grabación de la experiencia',
            'Foco en interacción, mediación y ambiente educativo',
        ],
    },
    {
        icon: Users,
        eyebrow: 'Módulo 3',
        title: 'Trabajo Colaborativo para el Desarrollo Profesional',
        body: 'Evidencia tu participación en instancias colaborativas con tu equipo pedagógico, y cómo esa colaboración impacta en tu práctica y en los aprendizajes de los párvulos. Contiene la Tarea 5.',
        gradient: 'from-emerald-500 to-teal-600',
        color: 'emerald',
        items: [
            'Tarea 5 · Trabajo colaborativo con el equipo',
            'Reflexión conjunta y transferencia a la práctica',
        ],
    },
    {
        icon: CheckCircle2,
        eyebrow: 'Estás lista',
        title: 'Todo tu recorrido es guiado por IA',
        body: 'Cada indicador tiene su rúbrica oficial y análisis con IA aislada de Parvularia. Elige un módulo, una tarea, un indicador y comienza tu borrador. Estamos contigo en cada paso.',
        gradient: 'from-violet-500 to-purple-600',
        color: 'violet',
    },
];

export default function ParvulariaTour({ active }) {
    // `active` true cuando el contexto es Parvularia (perfil o tester_override='parvularia')
    const [open, setOpen] = useState(false);
    const [step, setStep] = useState(0);

    useEffect(() => {
        if (!active) return;
        try {
            if (!localStorage.getItem(STORAGE_KEY)) {
                setStep(0);
                // Pequeño delay para no colisionar con el fade-in del dashboard
                const t = setTimeout(() => setOpen(true), 800);
                return () => clearTimeout(t);
            }
        } catch { /* localStorage no disponible */ }
    }, [active]);

    const close = () => {
        try { localStorage.setItem(STORAGE_KEY, new Date().toISOString()); } catch {}
        setOpen(false);
    };

    if (!active) return null;
    const slide = SLIDES[step];
    const Icon = slide.icon;
    const isLast = step === SLIDES.length - 1;
    const isFirst = step === 0;

    return (
        <>
            {/* Botón discreto para relanzar el tour desde el dashboard */}
            <button
                type="button"
                onClick={() => { setStep(0); setOpen(true); }}
                className="inline-flex items-center gap-1 text-xs font-medium text-fuchsia-700 hover:text-fuchsia-900 underline underline-offset-4"
                data-testid="parvularia-tour-relaunch"
            >
                <Sparkles className="h-3 w-3" />
                Ver recorrido guiado
            </button>

            <Dialog open={open} onOpenChange={(v) => { if (!v) close(); }}>
                <DialogContent className="max-w-lg p-0 overflow-hidden" data-testid="parvularia-tour-dialog">
                    <div className={cn(
                        'relative h-32 bg-gradient-to-br p-6 text-white',
                        slide.gradient
                    )}>
                        <div className="absolute inset-0 opacity-20"
                             style={{ backgroundImage: 'radial-gradient(circle at 20% 20%, white 1px, transparent 1px), radial-gradient(circle at 80% 60%, white 1px, transparent 1px)', backgroundSize: '40px 40px, 60px 60px' }} />
                        <div className="relative z-10 flex items-center gap-3">
                            <div className="grid place-items-center h-12 w-12 rounded-xl bg-white/20 backdrop-blur">
                                <Icon className="h-6 w-6" />
                            </div>
                            <div className="text-[10px] font-bold uppercase tracking-widest opacity-90">
                                {slide.eyebrow}
                            </div>
                        </div>
                    </div>

                    <div className="p-6 pt-4 space-y-4">
                        <DialogTitle className="font-heading text-xl font-bold text-slate-900 leading-tight">
                            {slide.title}
                        </DialogTitle>
                        <DialogDescription className="text-sm text-slate-700 leading-relaxed">
                            {slide.body}
                        </DialogDescription>

                        {slide.items && (
                            <ul className="space-y-2 pt-1">
                                {slide.items.map((it, i) => (
                                    <li key={i} className="flex items-start gap-2 text-sm text-slate-700">
                                        <CheckCircle2 className={cn(
                                            'h-4 w-4 mt-0.5 shrink-0',
                                            slide.color === 'indigo' && 'text-indigo-600',
                                            slide.color === 'rose' && 'text-rose-600',
                                            slide.color === 'emerald' && 'text-emerald-600',
                                        )} />
                                        <span>{it}</span>
                                    </li>
                                ))}
                            </ul>
                        )}

                        {/* Progress dots */}
                        <div className="flex items-center justify-center gap-1.5 pt-2">
                            {SLIDES.map((_, i) => (
                                <div
                                    key={i}
                                    className={cn(
                                        'h-1.5 rounded-full transition-all',
                                        i === step ? 'w-6 bg-slate-900' : 'w-1.5 bg-slate-300'
                                    )}
                                />
                            ))}
                        </div>

                        <div className="flex items-center justify-between gap-2 pt-2">
                            <Button
                                variant="ghost"
                                size="sm"
                                onClick={close}
                                data-testid="parvularia-tour-skip"
                            >
                                Saltar
                            </Button>
                            <div className="flex items-center gap-2">
                                {!isFirst && (
                                    <Button
                                        variant="outline"
                                        size="sm"
                                        onClick={() => setStep(step - 1)}
                                        data-testid="parvularia-tour-prev"
                                    >
                                        <ChevronLeft className="h-4 w-4" />
                                        Anterior
                                    </Button>
                                )}
                                {!isLast ? (
                                    <Button
                                        size="sm"
                                        className="bg-slate-900 hover:bg-slate-800 gap-1"
                                        onClick={() => setStep(step + 1)}
                                        data-testid="parvularia-tour-next"
                                    >
                                        Siguiente
                                        <ChevronRight className="h-4 w-4" />
                                    </Button>
                                ) : (
                                    <Button
                                        size="sm"
                                        className="bg-fuchsia-600 hover:bg-fuchsia-700 gap-1"
                                        onClick={close}
                                        data-testid="parvularia-tour-finish"
                                    >
                                        Empezar a trabajar
                                        <Sparkles className="h-4 w-4" />
                                    </Button>
                                )}
                            </div>
                        </div>
                    </div>
                </DialogContent>
            </Dialog>
        </>
    );
}
